import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { workshopService } from "@/services/workshop.service";
import { getErrorMessage } from "@/services/api";
import { WORKSHOP_SERVICE_CATALOG } from "@/config/constants";

export function RequiredServicesEditor({ workshop }) {
    const qc = useQueryClient();
    const disabled = workshop?.workshop_status === "aguardando_proposta"
        || workshop?.workshop_status === "aguardando_recebimento";

    const [selected, setSelected] = useState(WORKSHOP_SERVICE_CATALOG[0]);
    const [custom, setCustom] = useState("");
    const [notes, setNotes] = useState("");

    const add = useMutation({
        mutationFn: (payload) => workshopService.addService(workshop.id, payload),
        onSuccess: () => {
            toast.success("Serviço adicionado.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
            setCustom(""); setNotes("");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao adicionar serviço.")),
    });

    const remove = useMutation({
        mutationFn: (serviceId) => workshopService.removeService(workshop.id, serviceId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["workshop", workshop.id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover.")),
    });

    function handleAdd(e) {
        e.preventDefault();
        const name = selected === "Outro" ? custom.trim() : selected;
        if (!name) { toast.error("Informe o serviço."); return; }
        add.mutate({ name, notes: notes || null });
    }

    const items = workshop?.required_services || [];

    return (
        <div className="space-y-3" data-testid="required-services-editor">
            <div className="flex items-center gap-2 text-sm font-semibold">
                <Wrench className="h-4 w-4 text-muted-foreground" /> Serviços necessários
            </div>
            {!disabled && (
                <form onSubmit={handleAdd} className="grid gap-2 md:grid-cols-[240px_1fr_auto]">
                    <Select value={selected} onValueChange={setSelected}>
                        <SelectTrigger data-testid="service-picker"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {WORKSHOP_SERVICE_CATALOG.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="grid gap-2 md:grid-cols-2">
                        {selected === "Outro" ? (
                            <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Nome do serviço" data-testid="service-custom-name" />
                        ) : <div />}
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observação (opcional)" data-testid="service-notes" />
                    </div>
                    <Button type="submit" size="sm" disabled={add.isPending} data-testid="add-service-button">
                        <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
                    </Button>
                </form>
            )}
            {items.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                    Nenhum serviço cadastrado ainda.
                </div>
            ) : (
                <ul className="divide-y rounded-md border">
                    {items.map((s) => (
                        <li key={s.id} className="flex items-center justify-between p-2">
                            <div>
                                <div className="text-sm font-medium">{s.name}</div>
                                {s.notes && <div className="text-xs text-muted-foreground">{s.notes}</div>}
                            </div>
                            {!disabled && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-danger" onClick={() => remove.mutate(s.id)} data-testid={`remove-service-${s.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function RequiredPartsEditor({ workshop }) {
    const qc = useQueryClient();
    const disabled = workshop?.workshop_status === "aguardando_proposta"
        || workshop?.workshop_status === "aguardando_recebimento";

    const [description, setDescription] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [observation, setObservation] = useState("");

    const add = useMutation({
        mutationFn: (payload) => workshopService.addPart(workshop.id, payload),
        onSuccess: () => {
            toast.success("Peça adicionada.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
            setDescription(""); setQuantity(1); setObservation("");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao adicionar peça.")),
    });

    const remove = useMutation({
        mutationFn: (partId) => workshopService.removePart(workshop.id, partId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["workshop", workshop.id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover.")),
    });

    function handleAdd(e) {
        e.preventDefault();
        if (!description.trim()) { toast.error("Informe a descrição da peça."); return; }
        add.mutate({
            description: description.trim(),
            quantity: Math.max(1, Number(quantity) || 1),
            observation: observation || null,
        });
    }

    const items = workshop?.required_parts || [];

    return (
        <div className="space-y-3" data-testid="required-parts-editor">
            <div className="flex items-center gap-2 text-sm font-semibold">Peças necessárias</div>
            {!disabled && (
                <form onSubmit={handleAdd} className="grid gap-2 md:grid-cols-[1fr_120px_1fr_auto]">
                    <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Rolamento 6314" data-testid="part-description" />
                    <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Qtd." data-testid="part-quantity" />
                    <Input value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Observação (opcional)" data-testid="part-observation" />
                    <Button type="submit" size="sm" disabled={add.isPending} data-testid="add-part-button">
                        <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
                    </Button>
                </form>
            )}
            {items.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                    Nenhuma peça cadastrada ainda.
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase text-muted-foreground">
                        <tr className="border-b">
                            <th className="py-2 text-left">Descrição</th>
                            <th className="py-2 text-center">Qtd.</th>
                            <th className="py-2 text-left">Observação</th>
                            {!disabled && <th className="py-2" />}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-b last:border-0">
                                <td className="py-2">{p.description}</td>
                                <td className="py-2 text-center font-mono">{p.quantity}</td>
                                <td className="py-2 text-xs text-muted-foreground">{p.observation || "—"}</td>
                                {!disabled && (
                                    <td className="py-2 text-right">
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-danger" onClick={() => remove.mutate(p.id)} data-testid={`remove-part-${p.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
